import React from 'react';
import { TrackedLink } from 'components/common/TrackedLink';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { basePaths } from 'app-constants';
import lowerCase from 'lodash/fp/lowerCase';
import replace from 'lodash/fp/replace';
import flow from 'lodash/fp/flow';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';

const dasherize = flow(lowerCase, replace(/\s/g, '-'));

interface PractitionerDocumentLinkProps extends LinkHelperProps {
  doctype: string;
  slug: string;
}

interface PractitionerLinkProps extends LinkHelperProps {
  id?: string;
}

export type { PractitionerLinkProps };

export function parsePractitionerPath(path: string) {
  // Parse practitioner paths like /practitioner/{id}
  const practitionerPathRegex = new RegExp(
    `^${basePaths.practitioner}/([^/]+)$`,
  );
  const match = path.match(practitionerPathRegex);
  if (match) {
    return {
      id: match[1],
    };
  }
  return null;
}

export function PractitionerLink({ id, ...props }: PractitionerLinkProps) {
  const workspacePath = useWorkspacePath();
  const path = React.useMemo(() => {
    return workspacePath + `${basePaths.practitioner}/${id}`;
  }, [id, workspacePath]);

  return <TrackedLink to={path} {...props} />;
}

export function usePractitionerDocumentPath(
  slug: string,
  doctype?: string,
  query?: string | null,
  hash?: string | null,
) {
  const workspacePath = useWorkspacePath();

  const path = React.useMemo(() => {
    const parts = [basePaths.practitioner];

    if (doctype) {
      if (doctype === 'Workflows') {
        parts.push('workflow');
      } else {
        parts.push(dasherize(doctype));
      }
    }

    parts.push(slug);

    return workspacePath + parts.filter(Boolean).join('/');
  }, [slug, doctype, workspacePath]);

  return useQueryAndHash(path, query, hash);
}

export function PractitionerDocumentLink({
  to,
  slug,
  doctype,
  hash,
  query,
  ...props
}: PractitionerDocumentLinkProps) {
  const path = usePractitionerDocumentPath(slug, doctype, query, hash);
  return <TrackedLink to={to ? to : path} {...props} />;
}

interface PractitionerWorkflowLinkProps
  extends Omit<PractitionerDocumentLinkProps, 'doctype'> {
  practiceArea: string;
  practiceAreaSubtopic: string;
  practiceAreaSubcategory?: string;
}

function usePractitionerWorkflowPath(
  slug: string,
  practiceArea: string,
  practiceAreaSubtopic: string,
  practiceAreaSubcategory?: string,
  query?: string,
) {
  const workspacePath = useWorkspacePath();

  const path = React.useMemo(() => {
    const parts = [
      basePaths.practitioner,
      'area',
      practiceArea,
      practiceAreaSubtopic,
      ...(practiceAreaSubcategory ? [practiceAreaSubcategory] : []),
      'workflow',
      slug,
    ];

    const basePath = parts.filter(Boolean).join('/');
    return workspacePath + basePath;
  }, [
    slug,
    practiceArea,
    practiceAreaSubtopic,
    practiceAreaSubcategory,
    workspacePath,
  ]);

  return useQueryAndHash(path, query);
}

export function PractitionerWorkflowLink({
  to,
  slug,
  practiceArea,
  practiceAreaSubtopic,
  practiceAreaSubcategory,
  query,
  ...props
}: PractitionerWorkflowLinkProps) {
  const path = usePractitionerWorkflowPath(
    slug,
    practiceArea,
    practiceAreaSubtopic,
    practiceAreaSubcategory,
  );
  return <TrackedLink to={to ? to : path} {...props} />;
}
